import { useMemo, useState } from 'react';
import {
	SourceType,
	ReactMapProps,
	assetConfigCreator,
	ConfigLayer,
} from '@innovyze/inno-map';
import {
	DataServiceType,
	GeoJsonInputType,
} from '@innovyze/inno-map/map/services/types';

export interface MapLocationPosition {
	center: number[];
	zoom: number;
}
type MapLocationStateReturn = [
	MapLocationPosition,
	DataServiceType<unknown>[] | undefined,
	(data: string) => boolean,
	ConfigLayer[] | undefined,
];

const useMapLocationState = (): MapLocationStateReturn => {
	const [dataSources, setDataSources] = useState([
		{
			type: SourceType.GEOJSON,
			id: 'sensor',
			data: {} as GeoJsonInputType,
		},
	] as ReactMapProps['dataServices']);
	const [position, setPosition] = useState({
		center: [0, 0],
		zoom: 10,
	});

	// this function is to check if the lat and lng are valid before updating
	// the map. If the map is updated with bad coords it crashes
	// Latitude range in degrees is -90 and +90
	// Longitude is in the range -180 and +180
	function validateLatLng(_lat: number, _lng: number): boolean {
		let valid = false;
		if (_lat >= -90 && _lat <= 90 && _lng >= -180 && _lng <= 180) {
			valid = true;
		}
		return valid;
	}

	function updateMap(lat: number, lng: number) {
		setPosition({
			center: [lat, lng],
			zoom: 15,
		});
		setDataSources([
			{
				type: SourceType.GEOJSON,
				id: 'sensor',
				displayName: 'Location',
				data: {
					type: 'FeatureCollection',
					features: [
						{
							type: 'Feature',
							geometry: {
								type: 'Point',
								coordinates: [lat, lng],
							},
							properties: {
								_id: 'sensor',
								assetType: 'sensor',
							},
						},
					],
				} as GeoJsonInputType,
			},
		] as ReactMapProps['dataServices']);
	}

	function mapChange(data: string) {
		try {
			const coor = data.split(',');
			const [latitude, longitude] = [
				parseFloat(coor[0]),
				parseFloat(coor[1]),
			];
			if (coor.length == 2 && !isNaN(latitude) && !isNaN(longitude)) {
				// need to validate coordinates before updating the map or it crashes
				if (validateLatLng(latitude, longitude)) {
					updateMap(longitude, latitude);
					return true;
				}
			}
			return false;
		} catch (error) {
			return false;
		}
	}

	const layerConfig = useMemo(() => {
		const sensorDataSource = dataSources?.[0];
		if (sensorDataSource && Object.keys(sensorDataSource.data).length) {
			return [assetConfigCreator(sensorDataSource)];
		}
	}, [dataSources]);

	return [position, dataSources, mapChange, layerConfig];
};

export default useMapLocationState;
